import { LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import { Badge, Button, DatePicker, DatePickerProps, Form, Input, Progress, Spin, message } from 'antd';
import { useForm } from 'antd/es/form/Form';
import dayjs from 'dayjs';
import { t } from 'i18next';
import fileDownload from 'js-file-download';
import { useEffect, useRef, useState } from 'react';
import ExtraTab from 'src/app/components/ExtraTabcomponent/ExtraTab';
import FormComponent from 'src/app/components/FormComponent/FormComponent';
import showModal from 'src/app/components/ShowModalComponent/ShowModal';
import { socketHelper } from 'src/app/helpers';
import { formatDate } from 'src/app/helpers/time.helper';
import { useNumberMutation, useStartMutation } from 'src/app/redux/blackSystem/blackSystem.slice';
import { useAppSelector } from 'src/app/redux/hook';
import { axios } from 'src/app/utils';
import { finalUrlsConfig } from 'src/configs/url.config';
import { UploadIcon } from 'src/icons';

const now = dayjs();
const min = now.subtract(10, 'year');
const max = now.add(10, 'year');
function CourtRecordComponent() {
  // States
  const [dto, setDto] = useState<string>('');
  const [numberMessage, setNumberMessage] = useState<string | number>('');
  const [toDate, setToDate] = useState<string | undefined>();
  const [fromDate, setFromDate] = useState<string | undefined>();
  const [zipCount, setZipCount] = useState<number>(0);
  const [isExportLoading, setIsExportLoading] = useState<boolean>(false);
  const [allProgress, setAllProgress] = useState<any[]>([]);
  const [eventProgress, setEventProgress] = useState<any>({});
  const [startProcess, setStartProcess] = useState<string>('');

  const [downloadState, setDownloadState] = useState<{
    state: boolean;
    done: boolean;
    size: string;
  }>({
    state: false,
    done: false,
    size: '0.00',
  });

  // AntDState
  const [form] = useForm();
  const keyword = Form.useWatch('keyword', form);
  const fromDateValue = Form.useWatch('fromDate', form);
  const toDateValue = Form.useWatch('toDate', form);

  const [start] = useStartMutation();
  const [number, { isLoading: isNumberLoading }] = useNumberMutation();
  const { theme } = useAppSelector((state) => state.configContent);
  const debounceTimeoutRef: any = useRef(null);

  const numberTimeoutRef: any = useRef(null);
  const exportTimeoutRef: any = useRef(null);
  const startProcessRef = useRef(startProcess);

  useEffect(() => {
    socketHelper.createConnection({ path: '/oss-progress' });
  }, []);

  useEffect(() => {
    startProcessRef.current = startProcess;
  }, [startProcess]);

  useEffect(() => {
    const socketData = socketHelper.listenSocket('event_progress', (data) => {
      if (startProcessRef.current === data.job.id) {
        setEventProgress(data.job);
      }
    });
  }, [socketHelper]);

  useEffect(() => {
    if (isNumberLoading) {
      numberTimeoutRef.current = setTimeout(() => {
        message.error(t('blackSystemPage.stillWaitingForNumberResponse'), 5);
      }, 5000);
    } else {
      clearTimeout(numberTimeoutRef.current);
    }

    return () => {
      clearTimeout(numberTimeoutRef.current);
    };
  }, [isNumberLoading]);

  useEffect(() => {
    if (isExportLoading) {
      exportTimeoutRef.current = setTimeout(() => {
        message.error(t('blackSystemPage.stillWaitingForExportResponse'), 5);
      }, 5000);
    } else {
      clearTimeout(exportTimeoutRef.current);
    }

    return () => {
      clearTimeout(exportTimeoutRef.current);
    };
  }, [isExportLoading]);

  const handleNumberReq = () => {
    if (isNumberLoading) return;
    setDto(keyword);
    setNumberMessage('');
    setZipCount(0);
    setDownloadState({
      state: false,
      done: false,
      size: '0.00',
    });

    let data: any = { query: keyword };

    if (toDateValue && fromDateValue) {
      data = { ...data, ...(toDateValue && { toDate }), ...(fromDateValue && { fromDate }) };
    }

    number({ data, url: 'Court_Record' })
      .unwrap()
      .then((res) => {
        if (res.oldest_folder_date) {
          setNumberMessage(
            `${res.number} ${t('blackSystemPage.newsApi.numberMessageCount')} ${res.oldest_folder_date}`,
          );
          setZipCount(res.number);
        } else {
          setNumberMessage(() => {
            if (toDateValue && fromDateValue) {
              const formattedToDate = formatDate(toDateValue);
              const formattedFromDate = formatDate(fromDateValue);

              return `${res.number}, between ${formattedToDate} & ${formattedFromDate}`;
            }
            return `${res.number}`;
          });
          setZipCount(res.number);
        }
      })
      .catch(() => {
        setNumberMessage('');
        setZipCount(0);
        message.success(t('blackSystemPage.numberOperationFailed'), 5);
      });
  };

  useEffect(() => {
    if (keyword && keyword !== '') {
      debounceTimeoutRef.current = setTimeout(() => {
        handleNumberReq();
      }, 500);
    } else {
      setNumberMessage('');
    }

    return () => {
      clearTimeout(debounceTimeoutRef.current);
    };
  }, [keyword, toDateValue, fromDateValue]);

  const disableConditions = () => {
    if (isExportLoading) {
      return true;
    }
    if (zipCount === 0 || !zipCount) {
      return true;
    }
    if (!keyword) {
      return true;
    }
    return false;
  };

  const disabledDate = (current: dayjs.Dayjs, compareDate?: dayjs.Dayjs, isToDate?: boolean) => {
    const sevenYearsAgo = dayjs().subtract(7, 'year');

    if (isToDate && compareDate) {
      return current && (current.isAfter(dayjs().endOf('day')) || current.isBefore(compareDate));
    }

    if (current && current.isBefore(sevenYearsAgo, 'day')) {
      return true;
    }

    return current && current.isAfter(dayjs().endOf('day'));
  };

  const onFromDateChange: DatePickerProps['onChange'] = (date, dateString) => {
    let formattedDate;
    if (typeof dateString === 'string') {
      formattedDate = dateString.split(' ');
      const secondsFromFormattedDate = formattedDate.pop();
      const newSeconds = `${secondsFromFormattedDate}:00`;
      formattedDate.push(newSeconds);
      formattedDate = formattedDate.join('T');
    }
    setFromDate(formattedDate);
  };

  const onToDateChange: DatePickerProps['onChange'] = (_date, dateString) => {
    let formattedDate;
    if (typeof dateString === 'string') {
      formattedDate = dateString.split(' ');
      const secondsFromFormattedDate = formattedDate.pop();
      const newSeconds = `${secondsFromFormattedDate}:00`;
      formattedDate.push(newSeconds);
      formattedDate = formattedDate.join('T');
    }
    setToDate(formattedDate);
  };
  return (
    <div className="social-media">
      <div className="first-form">
        <div className="news-api-form">
          <FormComponent
            extraButton={
              <div className="extra-btns">
                <ExtraTab
                  onClick={() => {
                    if (zipCount > 0) {
                      showModal({
                        title: t('blackSystemPage.startStartOperation'),
                        content: <p>{t('blackSystemPage.startOperationWarnText')}</p>,
                        onSave: (modal) => {
                          start({ data: { query: dto }, url: 'Court_Record' })
                            .unwrap()
                            .then((payload) => {
                              // console.log('payload.job_id zipcount>0', payload.job_id);
                              setStartProcess(payload.job_id);
                            })
                            .catch(() => message.error(t('blackSystemPage.startOperationFailed'), 5));
                          modal.destroy();
                          message.success(t('blackSystemPage.startOperationSuccess'), 5);
                        },
                        okText: t('blackSystemPage.startOkText'),
                      });
                    } else {
                      message.success(t('blackSystemPage.startOperationSuccess'), 5);
                      start({ data: { query: dto }, url: 'Court_Record' })
                        .unwrap()
                        .then((payload) => {
                          // console.log('payload.job_id', payload.job_id);
                          setStartProcess(payload.job_id);
                        })
                        .catch(() => message.error(t('blackSystemPage.startOperationFailed'), 5));
                    }
                  }}
                  icon={null}
                  text={t('blackSystemPage.commonBtns.start')}
                  bgTheme
                  disabled={!keyword}
                />

                <ExtraTab
                  onClick={async () => {
                    setIsExportLoading(true);
                    try {
                      message.success(t('blackSystemPage.exportOperationSuccess'), 5);
                      await axios({
                        url: finalUrlsConfig().blackSystem('Court_Record').export,
                        method: 'POST',
                        data: { query: dto, ...(toDateValue && { toDate }), ...(fromDateValue && { fromDate }) },
                        responseType: 'blob',
                        onDownloadProgress: (progressEvent) => {
                          const bytesDownloaded = progressEvent.loaded;
                          const megabytesDownloaded = bytesDownloaded / (1024 * 1024);
                          if (megabytesDownloaded) {
                            if (megabytesDownloaded > 1) {
                              setIsExportLoading(false);
                            }
                            setDownloadState((prevState) => ({
                              ...prevState,
                              state: true,
                              size: `${megabytesDownloaded.toFixed(2)}`,
                            }));
                          }
                        },
                        blockSpinner: true,
                      })
                        .then((res: any) => {
                          setDownloadState({
                            size: '0.00',
                            state: false,
                            done: true,
                          });
                          const today = new Date();
                          const formattedDate = formatDate(today);
                          const fileName = dto || 'Court_Record';
                          fileDownload(res.data, `${fileName}-${formattedDate}.zip`);
                          setIsExportLoading(false);
                        })
                        .catch(() => {
                          message.error(t('blackSystemPage.exportOperationFailed'), 5);
                          setDownloadState({
                            done: false,
                            size: '0.00',
                            state: false,
                          });
                          setIsExportLoading(false);
                        });
                    } catch (error) {
                      setIsExportLoading(false);
                      console.log('error', error);
                    }
                  }}
                  icon={<UploadIcon />}
                  text={t('blackSystemPage.commonBtns.exportData')}
                  bgTheme
                  disabled={disableConditions()}
                />
                {/* <ExtraTab
                  onClick={() => {
                    number({ data: { query: dto }, url: 'Court_Record' })
                      .unwrap()
                      .then((res) => {
                        setNumberMessage(res);
                      })
                      .catch(() => {
                        setNumberMessage('');
                        message.success(t('blackSystemPage.numberOperationFailed'), 5);
                      });
                  }}
                  icon={null}
                  text={t('blackSystemPage.commonBtns.number')}
                  bgTheme
                  disabled={!dto}
                /> */}
              </div>
            }
            options={{ labelCol: { span: 9 }, form, size: 'middle' }}
          >
            <Form.Item
              className="form-with-btn"
              name="keyword"
              label={
                <div style={{ display: 'flex' }}>
                  {t('blackSystemPage.newsApi.form.addNewKeyword')}
                  <Badge offset={[10, 0]} />
                </div>
              }
              rules={[
                {
                  required: true,
                  message: t('errors.required'),
                },
              ]}
              initialValue=""
            >
              <Input bordered={false} type="text" />
            </Form.Item>
            <Form.Item label={t('blackSystemPage.newsApi.form.fromDate')} htmlFor="fromDate" name="fromDate">
              <DatePicker
                showTime
                variant="borderless"
                id="time"
                format="YYYY-MM-DD HH:mm"
                onChange={onFromDateChange}
                disabledDate={(current) => disabledDate(current)}
                minDate={min}
                maxDate={max}
                showHour={false}
                showNow={false}
              />
            </Form.Item>
            <Form.Item label={t('blackSystemPage.newsApi.form.toDate')} htmlFor="toDate" name="toDate">
              <DatePicker
                showTime
                variant="borderless"
                id="time"
                format="YYYY-MM-DD HH:mm"
                onChange={onToDateChange}
                minDate={min}
                maxDate={max}
                disabledDate={(current) => disabledDate(current, fromDateValue, true)}
                showHour={false}
                showNow={false}
              />
            </Form.Item>
          </FormComponent>
          {!dto && <p>{t('blackSystemPage.atLeastOneTopicNeeded')}</p>}
          <div className="resultAreas">
            {(downloadState.done || downloadState.state) && (
              <div
                className="resultArea"
                style={{
                  ...(theme.colors.quoteBgc && {
                    borderTop: `1px solid ${theme.colors.quoteBgc}`,
                    borderBottom: `1px solid ${theme.colors.quoteBgc}`,
                    color: `${theme.colors.quoteBgc}`,
                  }),
                }}
              >
                {downloadState.state ? (
                  <>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                    <p>{downloadState.size}mb</p>
                  </>
                ) : (
                  downloadState.done && (
                    <>
                      <Progress type="circle" percent={100} size={30} />
                      <p>{t('blackSystemPage.successfullyDownloaded')}</p>
                    </>
                  )
                )}
              </div>
            )}
            {numberMessage !== '' && !isNumberLoading && (
              <div className="resultAreas">
                <div
                  className="resultArea"
                  style={{
                    ...(theme.colors.quoteBgc && {
                      borderTop: `1px solid ${theme.colors.quoteBgc}`,
                      borderBottom: `1px solid ${theme.colors.quoteBgc}`,
                      color: `${theme.colors.quoteBgc}`,
                    }),
                  }}
                >
                  <Button
                    className="refetchBtn"
                    style={{ backgroundColor: `${theme.colors.quoteBgc}` }}
                    onClick={handleNumberReq}
                  >
                    {t('blackSystemPage.refetchBtn')} <ReloadOutlined style={{ fontSize: '1.25' }} />
                  </Button>
                  <p>
                    {t('blackSystemPage.newsApi.numberMessage')}
                    {numberMessage}
                  </p>
                </div>
                <Progress
                  size={80}
                  type="circle"
                  percent={eventProgress.progress}
                  strokeColor={eventProgress.progress === 100 ? 'green' : `${theme.colors.quoteBgc}`}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CourtRecordComponent;
